import React, { useState, useEffect } from 'react';
import { FiPrinter, FiSend, FiX } from 'react-icons/fi';
import { fetchBudgetLines, sendForecastEmail } from '../apiService';
import DashboardLayout from '../components/DashboardLayout.js';
import './Budget.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const BUDGET_CATEGORIES = ['Booth', 'Travel', 'Booth Expenses', 'Marketing Expenses'];

function Forecast() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [recipients, setRecipients] = useState(['']);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    async function load() {
      if (!user.userId) { setLoading(false); return; }
      const result = await fetchBudgetLines({ userId: user.userId });
      if (result.success) setItems(result.data || []);
      setLoading(false);
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group line items by event
  const groups = {};
  items.forEach(item => {
    const key = item.eventId || 'unassigned';
    if (!groups[key]) {
      groups[key] = { eventName: item.eventName || 'Unassigned', eventDate: item.eventDate || null, items: [] };
    }
    groups[key].items.push(item);
  });
  const groupKeys = Object.keys(groups);

  function monthIndexOf(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr + 'T00:00:00').getMonth();
  }

  function categoryTotal(groupItems, category) {
    return groupItems
      .filter(i => i.category === category)
      .reduce((s, i) => s + (parseFloat(i.budgetCost) || 0), 0);
  }

  function groupTotal(groupItems) {
    return groupItems.reduce((s, i) => s + (parseFloat(i.budgetCost) || 0), 0);
  }

  const grandTotal = items.reduce((s, i) => s + (parseFloat(i.budgetCost) || 0), 0);

  function openSendDialog() {
    setRecipients([user.email || '']);
    setSendResult(null);
    setShowSendDialog(true);
  }

  function updateRecipient(index, value) {
    const next = [...recipients];
    next[index] = value;
    setRecipients(next);
  }

  function addRecipientField() {
    setRecipients([...recipients, '']);
  }

  function removeRecipientField(index) {
    setRecipients(recipients.filter((_, i) => i !== index));
  }

  async function handleSendForecast() {
    const cleaned = recipients.map(r => r.trim()).filter(Boolean);
    if (cleaned.length === 0) {
      setSendResult({ ok: false, msg: 'Enter at least one email address.' });
      return;
    }
    setSending(true);
    setSendResult(null);
    const result = await sendForecastEmail(user.userId, cleaned);
    setSending(false);
    if (result.success) {
      setSendResult({ ok: true, msg: `Sent to ${result.sent} recipient${result.sent === 1 ? '' : 's'}.` });
    } else {
      setSendResult({ ok: false, msg: result.error || 'Failed to send.' });
    }
  }

  return (
    <DashboardLayout>
      <h1 className="page-title">Trade Show Forecast</h1>

      <div className="eval-card">
        <h2 className="eval-forecast-title">Trade Show - Forecast</h2>
        {loading ? (
          <p>Loading...</p>
        ) : groupKeys.length === 0 ? (
          <p>No budget items yet. Add line items on the Budget page and they will roll up here by show and month.</p>
        ) : (
          <div className="eval-forecast-grid">
            <table className="eval-forecast-table">
              <thead>
                <tr>
                  <th>SHOWS</th>
                  <th>Previous Year</th>
                  {MONTHS.map(m => <th key={m}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {groupKeys.map(key => {
                  const group = groups[key];
                  const monthIdx = monthIndexOf(group.eventDate);
                  return (
                    <React.Fragment key={key}>
                      <tr className="eval-forecast-show-header">
                        <td colSpan={MONTHS.length + 2}>
                          <strong>{group.eventName}{group.eventDate ? ` — ${group.eventDate}` : ''}</strong>
                        </td>
                      </tr>
                      {BUDGET_CATEGORIES.map(cat => {
                        const total = categoryTotal(group.items, cat);
                        if (total === 0) return null;
                        return (
                          <tr key={cat}>
                            <td>{cat}</td>
                            <td>-</td>
                            {MONTHS.map((m, idx) => (
                              <td key={m}>{monthIdx === idx ? `$${total.toLocaleString()}` : '-'}</td>
                            ))}
                          </tr>
                        );
                      })}
                      <tr className="eval-forecast-total">
                        <td><strong>Total:</strong></td>
                        <td colSpan={MONTHS.length + 1} style={{ textAlign: 'right' }}>
                          <strong>${groupTotal(group.items).toLocaleString()}</strong>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                <tr className="eval-forecast-total">
                  <td><strong>All Shows Total:</strong></td>
                  <td colSpan={MONTHS.length + 1} style={{ textAlign: 'right' }}>
                    <strong>${grandTotal.toLocaleString()}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <div className="eval-budget-actions">
          <button className="eval-print-btn" onClick={() => window.print()}>
            <FiPrinter size={16} /> Print Trade Show Forecast
          </button>
          <button className="eval-send-btn" onClick={openSendDialog}>
            <FiSend size={16} /> Send Trade Show Forecast
          </button>
        </div>
      </div>
      {showSendDialog && (
        <div className="event-form-overlay">
          <div className="event-form-card" style={{ maxWidth: '480px' }}>
            <div className="form-header">
              <h2>Send Forecast</h2>
              <button className="close-btn" onClick={() => setShowSendDialog(false)}><FiX size={20} /></button>
            </div>
            <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>
              Email the forecast for all shows. Your address is filled in by default; add others if you'd like.
            </p>
            {recipients.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                <input
                  type="email"
                  value={r}
                  onChange={e => updateRecipient(i, e.target.value)}
                  placeholder="name@example.com"
                  style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                />
                {recipients.length > 1 && (
                  <button className="eval-icon-btn delete" onClick={() => removeRecipientField(i)}><FiX size={16} /></button>
                )}
              </div>
            ))}
            <button
              onClick={addRecipientField}
              style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: '13px', padding: '4px 0', marginBottom: '12px' }}
            >
              + Add another recipient
            </button>
            {sendResult && (
              <p style={{ fontSize: '13px', color: sendResult.ok ? '#15803d' : '#dc2626', margin: '8px 0' }}>
                {sendResult.msg}
              </p>
            )}
            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={() => setShowSendDialog(false)}>Close</button>
              <button type="button" className="submit-btn" onClick={handleSendForecast} disabled={sending}>
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default Forecast;
